import React from 'react';
import { connect } from 'react-redux';
import * as Immutable from 'immutable';

import { RootState } from 'reducers/rootReducer';
import { Point, CoordinatePoint, isPoint } from 'types/point';
import { selectors as editModeSelectors } from 'ducks/editMode';
import { selectors as drawSelectors, DrawState } from 'ducks/draw/draw';
import SelectedSegmentLine from 'components/sketch/SelectedSegment/SelectedSegmentLine';

interface StateProps {
  readonly isDrawing: boolean;
  readonly isEditMode: boolean;
  readonly isMovingMode: boolean;
  readonly drawState: DrawState;
}

type Props = StateProps;

const AlignmentLines = ({
  isDrawing, isEditMode, drawState, isMovingMode
}: Props): JSX.Element | null => {
  const {
    endPoint, anchorPoint: curserPoint, alignedPoints, alignedWalls
  } = drawState;

  // If we are not aligning (ie: not drawing, not moving, not preparing to draw), return
  if (!isDrawing && !isEditMode && !isMovingMode) {
    return null;
  }

  // If there are no alignments to draw, return
  const areThereDrawAlignments = (alignedPoints && alignedPoints.length > 0);
  const areThereSelectMoveAlignments = (alignedWalls && alignedWalls.length > 0);
  if (!areThereDrawAlignments && !areThereSelectMoveAlignments) {
    return null;
  }

  let alignedPointsLines: Immutable.List<Point | CoordinatePoint>[] = [];

  // When aligning the first point, isEditMode = true, and isDrawing = false.
  // When drawing further points, isDrawing = true, and isEditMode = false.
  if ((isDrawing || isEditMode) && areThereDrawAlignments) {
    // when drawing the first point, use the curserPoint
    // otherwise, use the endPoint.
    const activePoint = (isDrawing) ? endPoint : curserPoint;
    if (!activePoint) {
      return null;
    }

    alignedPointsLines = alignedPoints.map(alignedPoint => {
      const currentPoint = { ...activePoint };

      // Because there is a range of alignment detection, ensure the alignment lines are straight
      if (Math.abs(alignedPoint.x - currentPoint.x) > Math.abs(alignedPoint.y - currentPoint.y)) {
        currentPoint.y = alignedPoint.y;
      } else {
        currentPoint.x = alignedPoint.x;
      }

      // return a wall from the activePoint, to the alignedPoint.
      return Immutable.List<Point | CoordinatePoint>([alignedPoint, currentPoint]);
    });
  } else if (isMovingMode && areThereSelectMoveAlignments) {
    // when selecting a selection, isMovingMode = true
    // when moving a selection, isMovingMode = true
    alignedPointsLines = alignedWalls.filter(x => x.first().tag !== 'hideAlignment');
  }

  return (
    <>
      {
        alignedPointsLines.map(alignedPointLine => {
          const first = alignedPointLine.get(0)!;
          return (
            <SelectedSegmentLine
              key={isPoint(first) ? first.pointId : undefined}
              wallPoints={alignedPointLine}
              isAlignmentLine
            />
          );
        })
      }
    </>
  );
};

export default connect((state: RootState): StateProps => ({
  isDrawing: editModeSelectors.isDrawingMode(state),
  isEditMode: editModeSelectors.isEditMode(state),
  isMovingMode: editModeSelectors.isMovingMode(state),
  drawState: drawSelectors.getDrawState(state),
}))(AlignmentLines);
